import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import Layout from "@/components/Layout";
import CaptchaDialog from "@/components/CaptchaDialog";
import {
  User,
  Mail,
  Phone,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Shield,
  UserPlus,
  MailCheck
} from "lucide-react";
import { userApi } from "@/api/modules/user";

const Register = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    username: "",
    nickname: "",
    email: "",
    phone: "",
    company: "",
    profession: "",
    smsCode: ""
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string>("");
  const [downloadUrl, setDownloadUrl] = useState<string>("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({
    username: "",
    nickname: "",
    email: "",
    phone: "",
    company: "",
    profession: "",
    smsCode: ""
  });

  const [isCaptchaDialogOpen, setIsCaptchaDialogOpen] = useState(false);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const validateStep1 = async () => {
    const newErrors: Record<string, string> = {};

    if (!formData.phone.trim()) {
      newErrors.phone = "请输入手机号码";
    } else if (!/^1[3-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = "请输入有效的手机号码";
    }

    if (!formData.smsCode.trim()) {
      newErrors.smsCode = "请输入短信验证码";
    } else if (formData.smsCode.length !== 6) {
      newErrors.smsCode = "请输入6位短信验证码";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.username.trim()) {
      newErrors.username = "请输入用户名";
    } else if (formData.username.length < 3) {
      newErrors.username = "用户名至少需要3个字符";
    }

    if (!formData.nickname.trim()) {
      newErrors.nickname = "请输入昵称";
    }

    if (!formData.email.trim()) {
      newErrors.email = "请输入邮箱地址";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "请输入有效的邮箱地址";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = async () => {
    setApiError("");

    if (step === 1) {
      if (await validateStep1()) {
        setIsLoading(true);
        try {
          /** 短信验证码登录 */
          const response = await userApi.codeLogin({
            phone: formData.phone,
            smsCode: formData.smsCode,
          });

          if (response?.code === 500) {
            setApiError(`验证失败：${response.msg ? ': ' + response.msg : ''}`);
            setIsLoading(false);
            return;
          }
          /** 判别是否需要注册 */
          if (response?.code === 200 && response?.data.needRegister) {
            setStep(3);
          }else {
            setDownloadUrl(response?.data.downloadUrl);
            setIsSubmitted(true);
          }
          /** 存入token */
          localStorage.setItem("token", response?.data.token);
          sessionStorage.setItem("token", response?.data.token)
        } catch (error) {
          setApiError(error.response?.data?.message || "验证失败，请稍后重试");
        } finally {
          setIsLoading(false);
        }
      }
    }
  };

  const handlePrevStep = () => {
    setApiError("");
    if (step > 1) {
      setStep(1);
    }
  };

  const handleDownload = () => {
    if (downloadUrl && !isDownloading) {
      try {
        setIsDownloading(true);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (error) {
        console.error('下载失败:', error);
        alert('下载失败，请稍后重试');
        setIsDownloading(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError("");

    if (validateStep3()) {
      setIsLoading(true);

      try {
        const response = await userApi.createUser({
          username: formData.username,
          nickname: formData.nickname,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          profession: formData.profession,
          smsCode: formData.smsCode
        });

        if (response?.code === 500) {
          setApiError(`注册失败${response.msg ? ': ' + response.msg : ''}`);
          setStep(1);
          setIsLoading(false);
          return;
        }

        if (response?.data) {
          setDownloadUrl(response.data.downloadUrl);
        }

        setIsSubmitted(true);
      } catch (error) {
        setApiError(error.response?.data?.message || "注册失败，请稍后重试");
        console.error("注册失败:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  /**
   * 验证码逻辑--start---
   */
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsSent, setSmsSent] = useState(false);

  // 计算手机号是否有效（示例）
  const isPhoneValid = /^1[3-9]\d{9}$/.test(formData.phone);

  // 打开图形验证码弹窗
  const handleSendSmsCode = () => {
    if (!isPhoneValid) {
      setErrors(prev => ({...prev, phone: "请输入有效的手机号码"}));
      return;
    }
    setIsCaptchaDialogOpen(true);
  };

  // 图形验证成功后发送短信验证码
  const handleCaptchaVerifySuccess = async (captchaUuid: string, captchaCode: string) => {
    try {
      const response = await userApi.sendSmsCode({
        phone: formData.phone,
        uuid: captchaUuid,
        code: captchaCode,
        clientId: "pc"
      });

      if (response?.code === 500) {
        setApiError(`发送失败${response.msg ? ': ' + response.msg : ''}`);
        return;
      }

      // 设置倒计时（60秒）
      setSmsCountdown(60);
      setSmsSent(true);
      setApiError("");

      // 启动倒计时
      const timer = setInterval(() => {
        setSmsCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error("发送验证码失败:", error);
      setApiError("发送验证码失败，请稍后重试");
    }
  };
  /** 验证码逻辑--end--- */

  if (isSubmitted) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-20">
          <Card className="max-w-4xl w-full mx-auto">
            <CardContent className="p-12 text-center">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                验证成功，可点击直接下载！
              </h2>

              <div className="flex flex-col sm:flex-row gap-6 justify-center mt-8">
                <Button onClick={() => window.location.href = "/"} size="lg">
                  返回首页
                </Button>
                {downloadUrl && (
                  <Button variant="outline" onClick={handleDownload} disabled={isDownloading} size="lg">
                    {isDownloading ? '下载中...' : '下载'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="bg-gradient-to-br from-purple-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            产品下载
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            下载See系列光学仿真软件，体验专业的光学仿真解决方案
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between mb-4">
                <CardTitle className="text-2xl">
                  {step === 1 && "验证手机号"}
                  {step === 3 && "完善信息"}
                </CardTitle>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <span className={`px-3 py-1 rounded-full ${step === 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>1</span>
                  <span className={`px-3 py-1 rounded-full ${step === 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>2</span>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                {step === 1 && <Shield className="w-4 h-4" />}
                {step === 3 && <UserPlus className="w-4 h-4" />}
                <span>
                  {step === 1 && "请输入手机号并完成短信验证码验证"}
                  {step === 3 && "为了更好的产品服务，请您填写一下信息"}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={step === 3 ? handleSubmit : (e) => { e.preventDefault(); handleNextStep(); }}>
                <div className="space-y-6">
                  {step === 1 && (
                      <>
                        <div>
                          <Label htmlFor="phone" className="flex items-center space-x-2 mb-2">
                            <Phone className="w-4 h-4"/>
                            <span>手机号码 *</span>
                          </Label>
                          <div className="flex items-center space-x-2">
                            <Input
                                id="phone"
                                value={formData.phone}
                                onChange={(e) => handleInputChange("phone", e.target.value)}
                                placeholder="请输入手机号码"
                                className={`flex-1 ${errors.phone ? "border-red-500" : ""}`}
                            />
                            <Button
                                type="button"
                                onClick={handleSendSmsCode}
                                disabled={!isPhoneValid || smsCountdown > 0}
                                className="whitespace-nowrap"
                            >
                              {smsCountdown > 0 ? `${smsCountdown}秒后重发` : "发送验证码"}
                            </Button>
                          </div>
                          {errors.phone && (
                              <p className="text-sm text-red-500 mt-1">{errors.phone}</p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="smsCode" className="flex items-center space-x-2 mb-2">
                            <MailCheck className="w-4 h-4"/>
                            <span>短信验证码 *</span>
                          </Label>
                          <Input
                              id="smsCode"
                              value={formData.smsCode}
                              onChange={(e) => handleInputChange("smsCode", e.target.value)}
                              placeholder="请输入短信验证码"
                              className={errors.smsCode ? "border-red-500" : ""}
                              maxLength={6}
                          />
                          {errors.smsCode && (
                              <p className="text-sm text-red-500 mt-1">{errors.smsCode}</p>
                          )}
                          {formData.phone && smsSent && (
                              <p className="text-sm text-gray-600 mt-2">
                                验证码已发送至 {formData.phone}
                              </p>
                          )}
                        </div>
                      </>
                  )}

                  {step === 3 && (
                      <>
                        <div>
                          <Label htmlFor="username" className="flex items-center space-x-2 mb-2">
                            <User className="w-4 h-4"/>
                            <span>用户名 *</span>
                          </Label>
                          <Input
                              id="username"
                              value={formData.username}
                              onChange={(e) => handleInputChange("username", e.target.value)}
                              placeholder="请输入用户名"
                              className={errors.username ? "border-red-500" : ""}
                          />
                          {errors.username && (
                              <p className="text-sm text-red-500 mt-1">{errors.username}</p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="nickname" className="flex items-center space-x-2 mb-2">
                            <UserPlus className="w-4 h-4"/>
                            <span>昵称 *</span>
                          </Label>
                          <Input
                              id="nickname"
                              value={formData.nickname}
                              onChange={(e) => handleInputChange("nickname", e.target.value)}
                              placeholder="请输入昵称"
                              className={errors.nickname ? "border-red-500" : ""}
                          />
                          {errors.nickname && (
                              <p className="text-sm text-red-500 mt-1">{errors.nickname}</p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="email" className="flex items-center space-x-2 mb-2">
                            <Mail className="w-4 h-4"/>
                            <span>邮箱 *</span>
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          placeholder="请输入邮箱地址"
                          className={errors.email ? "border-red-500" : ""}
                        />
                        {errors.email && (
                          <p className="text-sm text-red-500 mt-1">{errors.email}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="company" className="flex items-center space-x-2 mb-2">
                          <UserPlus className="w-4 h-4" />
                          <span>公司</span>
                        </Label>
                        <Input
                          id="company"
                          value={formData.company}
                          onChange={(e) => handleInputChange("company", e.target.value)}
                          placeholder="请输入公司名称"
                          className={errors.company ? "border-red-500" : ""}
                        />
                        {errors.company && (
                          <p className="text-sm text-red-500 mt-1">{errors.company}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="profession" className="flex items-center space-x-2 mb-2">
                          <UserPlus className="w-4 h-4" />
                          <span>职业</span>
                        </Label>
                        <Input
                          id="profession"
                          value={formData.profession}
                          onChange={(e) => handleInputChange("profession", e.target.value)}
                          placeholder="请输入职业"
                          className={errors.profession ? "border-red-500" : ""}
                        />
                        {errors.profession && (
                          <p className="text-sm text-red-500 mt-1">{errors.profession}</p>
                        )}
                      </div>
                    </>
                  )}

                  {apiError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                      {apiError}
                    </div>
                  )}

                  <div className="flex items-center justify-between space-x-4">
                    {step > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handlePrevStep}
                        className="flex-1"
                      >
                        <ArrowLeft className="mr-2 w-4 h-4" />
                        上一步
                      </Button>
                    )}
                    <Button
                      type="submit"
                      className="flex-1"
                      disabled={isLoading}
                    >
                      {isLoading ? "处理中..." : (
                        <>
                          {step === 3 ? "注册下载" : "下一步"}
                          {step < 3 && <ArrowRight className="ml-2 w-4 h-4" />}
                        </>
                      )}
                    </Button>
                  </div>

                  {/* <div className="text-center text-sm text-gray-600">
                    已有账户？ <Link to="/login" className="text-blue-600 hover:underline font-medium">去登录</Link>
                  </div> */}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 图形验证码弹窗 */}
      <CaptchaDialog
        isOpen={isCaptchaDialogOpen}
        onClose={() => setIsCaptchaDialogOpen(false)}
        onVerifySuccess={handleCaptchaVerifySuccess}
        phone={formData.phone}
      />
    </Layout>
  );
};

export default Register;
